import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Preview as PreviewIcon,
    Clear as ClearIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import emailService from '../services/emailService';

const HtmlPreview = () => {
    const [htmlCode, setHtmlCode] = useState('');
    const [previewHtml, setPreviewHtml] = useState('');
    
    // Email state
    const [emailSubject, setEmailSubject] = useState('');
    const [emailRecipient, setEmailRecipient] = useState('');
    const [emailStatus, setEmailStatus] = useState(null); // 'sending', 'success', 'error'
    const [emailMessage, setEmailMessage] = useState('');

    const handlePreview = () => {
        setPreviewHtml(htmlCode);
    };

    const handleClear = () => {
        setHtmlCode('');
        setPreviewHtml('');
    };

    const handleSendEmail = async () => {
        if (!htmlCode.trim() || !emailSubject.trim() || !emailRecipient.trim()) {
            setEmailStatus('error');
            setEmailMessage('Please fill in all fields: HTML content, subject, and recipient email.');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailRecipient)) {
            setEmailStatus('error');
            setEmailMessage('Please enter a valid email address.');
            return;
        }

        setEmailStatus('sending');
        setEmailMessage('');

        try {
            const emailData = {
                subject: emailSubject,
                recipient: emailRecipient,
                htmlContent: htmlCode
            };

            const result = await emailService.sendEmail(emailData);
            
            setEmailStatus('success');
            setEmailMessage(`Email sent successfully to ${result.recipient}!`);
            
            // Clear form after successful send
            setTimeout(() => {
                setEmailSubject('');
                setEmailRecipient('');
                setEmailStatus(null);
                setEmailMessage('');
            }, 3000);

        } catch (error) {
            setEmailStatus('error');
            setEmailMessage(error.message || 'Failed to send email. Please try again.');
        }
    };

    const openInNewTab = () => {
        if (!htmlCode.trim()) return;
        
        const htmlContent = `
            <!DOCTYPE html>
            <html>
                <head>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1">
                    <title>HTML Preview</title>
                    <style>
                        body { margin: 0; padding: 16px; font-family: Arial, sans-serif; }
                        img { max-width: 100%; height: auto; }
                    </style>
                </head>
                <body>
                    ${htmlCode}
                </body>
            </html>
        `;
        
        const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(htmlContent);
        window.open(dataUrl, '_blank');
    };

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>
                HTML Preview
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Enter your HTML code below and click Preview to see the rendered HTML.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Text Input Area */}
                <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={htmlCode}
                    onChange={(e) => setHtmlCode(e.target.value)}
                    placeholder="Enter your HTML code here..."
                    variant="outlined"
                    sx={{
                        fontFamily: 'monospace',
                        '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                        },
                    }}
                />

                {/* Process Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handlePreview}
                        startIcon={<PreviewIcon />}
                        size="large"
                        color="primary"
                    >
                        Preview
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        startIcon={<ClearIcon />}
                        size="large"
                        color="error"
                    >
                        Clear
                    </Button>
                </Box>

                {/* Preview Section */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Preview
                    </Typography>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            minHeight: '300px',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: previewHtml || '<div style="display: flex; align-items: center; justify-content: center; height: 300px; color: #666; font-size: 16px; text-align: center; padding: 20px;">Enter HTML code above and click "Preview" to see the rendered HTML.</div>'
                            }}
                            style={{
                                width: '100%',
                                minHeight: '500px',
                                backgroundColor: '#ffffff',
                                overflow: 'auto',
                                padding: '16px'
                            }}
                        />
                    </Paper>
                </Box>

                {/* Email Section */}
                <Box sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="h6" gutterBottom>
                        Send as Email
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Send the HTML content above as an email to any recipient.
                    </Typography>

                    {/* Email Status Alert */}
                    {emailStatus && (
                        <Alert 
                            severity={
                                emailStatus === 'success' ? 'success' : 
                                emailStatus === 'sending' ? 'info' :
                                'error'
                            }
                            sx={{ mb: 2 }}
                            onClose={() => {
                                setEmailStatus(null);
                                setEmailMessage('');
                            }}
                        >
                            {emailStatus === 'sending' ? 'Sending email...' : emailMessage}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Subject Field */}
                        <TextField
                            label="Email Subject"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            placeholder="Enter email subject..."
                            variant="outlined"
                            fullWidth
                            disabled={emailStatus === 'sending'}
                        />

                        {/* Recipient Field */}
                        <TextField
                            label="Recipient Email"
                            value={emailRecipient}
                            onChange={(e) => setEmailRecipient(e.target.value)}
                            placeholder="Enter recipient email address..."
                            variant="outlined"
                            fullWidth
                            type="email"
                            disabled={emailStatus === 'sending'}
                        />

                        {/* Send Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                                variant="contained"
                                onClick={handleSendEmail}
                                startIcon={emailStatus === 'sending' ? 
                                    <CircularProgress size={16} color="inherit" /> : 
                                    <SendIcon />
                                }
                                size="large"
                                color="primary"
                                disabled={emailStatus === 'sending' || !htmlCode.trim()}
                                sx={{ minWidth: 120 }}
                            >
                                {emailStatus === 'sending' ? 'Sending...' : 'Send Email'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default HtmlPreview;